// Client-side image cropping utility that matches viewport exactly
// This ensures the cropped result matches what the user sees in the viewport

export interface CropCoordinates {
  x: number;      // Pan X offset from center
  y: number;      // Pan Y offset from center  
  scale: number;  // Zoom scale factor
  viewportWidth: number;
  viewportHeight: number;
  // Offset coordinates for dashed border area
  offsetX?: number; // X offset where crop area starts
  offsetY?: number; // Y offset where crop area starts
  // Inner crop area within the viewport (as percentages 0-1)
  innerCrop?: {
    left: number;   // Left edge as percentage of viewport width
    top: number;    // Top edge as percentage of viewport height
    width: number;  // Width as percentage of viewport width
    height: number; // Height as percentage of viewport height
  };
}

/**
 * Crops an image using HTML5 Canvas to exactly match the viewport
 * @param imageUrl - The source image URL
 * @param cropCoords - The crop coordinates from the viewport
 * @returns Promise<Blob> - The cropped image as a blob
 */
/**
 * Generate Cloudinary crop URL from viewport coordinates
 * This approach is more reliable than client-side canvas cropping
 */
export function generateCloudinaryCropUrl(
  originalCloudinaryUrl: string,
  cropCoords: CropCoordinates,
  naturalWidth?: number,
  naturalHeight?: number,
  renderedWidth?: number,
  renderedHeight?: number,
  background?: string,
  skipScaling?: boolean
): string {
  if (!originalCloudinaryUrl.includes('cloudinary.com')) {
    console.warn('⚠️ [Cloudinary] Not a Cloudinary URL, cannot apply transformations:', originalCloudinaryUrl);
    console.log('🔄 [Cloudinary] Returning original URL for non-Cloudinary source');
    return originalCloudinaryUrl;
  }

  // CRITICAL FIX: Account for CSS scaling of the image before transform
  // The image element is scaled down by CSS before the transform is applied
  // We need to convert from rendered coordinates to natural coordinates
  
  // Calculate the CSS scale factor
  let cssScaleX = 1;
  let cssScaleY = 1;
  
  if (naturalWidth && naturalHeight && renderedWidth && renderedHeight) {
    cssScaleX = renderedWidth / naturalWidth;
    cssScaleY = renderedHeight / naturalHeight;
    console.log('🎯 [CSS Scale] Detected CSS scaling:', {
      natural: { width: naturalWidth, height: naturalHeight },
      rendered: { width: renderedWidth, height: renderedHeight },
      cssScale: { x: cssScaleX, y: cssScaleY }
    });
  }
  
  // The coordinate transformation now needs to account for CSS scaling:
  // 1. Image is CSS-scaled from natural size to rendered size
  // 2. Then CSS transform is applied: translate(x, y) scale(s)
  // 
  // A point in natural image coordinates (natX, natY) appears at screen position:
  //   renderedX = natX * cssScaleX
  //   renderedY = natY * cssScaleY  
  //   screenX = renderedX * scale + x = (natX * cssScaleX) * scale + x
  //   screenY = renderedY * scale + y = (natY * cssScaleY) * scale + y
  //
  // Inversely, screen point (screenX, screenY) → natural coordinates:
  //   renderedX = (screenX - x) / scale
  //   renderedY = (screenY - y) / scale
  //   natX = renderedX / cssScaleX = ((screenX - x) / scale) / cssScaleX
  //   natY = renderedY / cssScaleY = ((screenY - y) / scale) / cssScaleY
  
  // Map the dashed border area to image coordinates
  // The dashed border represents the exact crop area we want to extract
  // We need to account for any offset where the dashed border appears within the viewport
  
  // Check if there's additional offset information in cropCoords
  const offsetX = cropCoords.offsetX || 0;
  const offsetY = cropCoords.offsetY || 0;
  
  const topLeftImageX = ((offsetX - cropCoords.x) / cropCoords.scale) / cssScaleX;
  const topLeftImageY = ((offsetY - cropCoords.y) / cropCoords.scale) / cssScaleY;
  const bottomRightImageX = ((offsetX + cropCoords.viewportWidth - cropCoords.x) / cropCoords.scale) / cssScaleX;
  const bottomRightImageY = ((offsetY + cropCoords.viewportHeight - cropCoords.y) / cropCoords.scale) / cssScaleY;

  // These should be in image pixel coordinates
  let finalX = topLeftImageX;
  let finalY = topLeftImageY;
  let finalWidth = bottomRightImageX - topLeftImageX;
  let finalHeight = bottomRightImageY - topLeftImageY;
  
  // Apply inner crop if specified (as percentages of the viewport crop area)
  if (cropCoords.innerCrop) {
    const innerOffsetX = cropCoords.innerCrop.left * finalWidth;
    const innerOffsetY = cropCoords.innerCrop.top * finalHeight;
    const innerWidth = cropCoords.innerCrop.width * finalWidth;
    const innerHeight = cropCoords.innerCrop.height * finalHeight;
    
    finalX = finalX + innerOffsetX;
    finalY = finalY + innerOffsetY;
    finalWidth = innerWidth;
    finalHeight = innerHeight;
  }

  // Handle coordinates that extend beyond image bounds
  // Cloudinary expects positive crop coordinates within the image
  // Use Math.floor/ceil for better precision with fractional coordinates
  let cropX = Math.max(0, Math.floor(finalX));
  let cropY = Math.max(0, Math.floor(finalY));
  
  // Adjust width/height if crop area was clipped
  // Use Math.ceil for dimensions to avoid cutting off pixels
  let cropW = Math.ceil(finalWidth);
  let cropH = Math.ceil(finalHeight);
  
  // If coordinates were negative, reduce the crop dimensions accordingly
  if (finalX < 0) {
    cropW = Math.round(finalWidth + finalX); // Reduce width by the amount we were negative
  }
  if (finalY < 0) {
    cropH = Math.round(finalHeight + finalY); // Reduce height by the amount we were negative  
  }
  
  // Clamp dimensions to image bounds if we know them
  if (naturalWidth && naturalHeight) {
    cropW = Math.min(cropW, naturalWidth - cropX);
    cropH = Math.min(cropH, naturalHeight - cropY);
  }
  
  // Ensure positive dimensions
  cropW = Math.max(1, cropW);
  cropH = Math.max(1, cropH);
  
  // No scaling calculations needed - we'll use the natural crop dimensions
  
  console.log('🌤️ [Cloudinary Crop] Coordinate calculation:', {
    viewport: { width: cropCoords.viewportWidth, height: cropCoords.viewportHeight },
    transform: { x: cropCoords.x, y: cropCoords.y, scale: cropCoords.scale },
    offset: { x: offsetX, y: offsetY },
    cssScale: { x: cssScaleX, y: cssScaleY },
    imageCorners: {
      topLeft: { x: topLeftImageX, y: topLeftImageY },
      bottomRight: { x: bottomRightImageX, y: bottomRightImageY }
    },
    calculatedCrop: { x: finalX, y: finalY, width: finalWidth, height: finalHeight },
    cloudinaryParams: { x: cropX, y: cropY, width: cropW, height: cropH },
    innerCrop: cropCoords.innerCrop,
    naturalSize: naturalWidth && naturalHeight ? { width: naturalWidth, height: naturalHeight } : 'unknown',
    coordinateAssumption: 'Maps offset viewport area to image coords - should match dashed border area'
  });
  
  // 🚨 DEBUGGING CROP DIMENSION ISSUES
  if (cropW > 3000 || cropH > 3000) {
    console.error('🚨 [DEBUG 4000x4000] Large crop dimensions detected!', {
      cropW,
      cropH,
      finalWidth,
      finalHeight,
      viewportWidth: cropCoords.viewportWidth,
      viewportHeight: cropCoords.viewportHeight,
      scale: cropCoords.scale,
      cssScaleX,
      cssScaleY,
      naturalWidth,
      naturalHeight,
      renderedWidth,
      renderedHeight
    });
  }
  
  // 🚨 DEBUGGING TINY CROP DIMENSIONS (likely markup image issue)
  if (cropW < 10 || cropH < 10) {
    console.error('🚨 [DEBUG TINY CROP] Very small crop dimensions detected - likely markup image issue!', {
      originalUrl: originalCloudinaryUrl.substring(0, 100) + '...',
      cropW,
      cropH,
      finalWidth,
      finalHeight,
      viewportWidth: cropCoords.viewportWidth,
      viewportHeight: cropCoords.viewportHeight,
      scale: cropCoords.scale,
      cssScaleX,
      cssScaleY,
      naturalWidth,
      naturalHeight,
      renderedWidth,
      renderedHeight,
      coordinatesUsed: {
        topLeftImageX,
        topLeftImageY,
        bottomRightImageX, 
        bottomRightImageY,
        finalX,
        finalY
      }
    });
    
    // For markup images, if we get tiny dimensions, it's likely because the image 
    // dimensions are not available. Return original URL instead of broken crop.
    if (originalCloudinaryUrl.includes('/collections/markup/')) {
      console.warn('⚠️ [MARKUP CROP] Returning original markup URL due to tiny crop dimensions');
      return originalCloudinaryUrl;
    }
  }

  // Build Cloudinary transformation URL
  const cropTransform = `c_crop,x_${cropX},y_${cropY},w_${cropW},h_${cropH}`;
  
  // Add background if specified (for masks)
  const backgroundTransform = background ? `b_${background}` : '';
  
  // Build transformations array - only include crop and quality, no scaling
  const transformationParts = [cropTransform];
  
  // Add quality parameters for better image quality
  const qualityTransform = skipScaling ? 'q_100,f_auto' : 'q_auto,f_auto'; // Higher quality for Canva
  transformationParts.push(qualityTransform);
  
  if (backgroundTransform) {
    transformationParts.push(backgroundTransform);
  }
  
  const transformations = transformationParts.join('/');
  
  // Handle different Cloudinary URL formats
  if (originalCloudinaryUrl.includes('/image/upload/')) {
    const uploadIndex = originalCloudinaryUrl.indexOf('/image/upload/');
    const beforeUpload = originalCloudinaryUrl.substring(0, uploadIndex + '/image/upload/'.length);
    const afterUpload = originalCloudinaryUrl.substring(uploadIndex + '/image/upload/'.length);
    
    // Look for version pattern (/v followed by digits)
    const versionMatch = afterUpload.match(/^(.*?)(\/?v\d+\/.*)/);
    if (versionMatch) {
      // URL has version: /image/upload/[existing_transforms]/v123456/path
      // Replace existing transforms with our new ones
      return `${beforeUpload}${transformations}/${versionMatch[2].replace(/^\//, '')}`;
    } else {
      // No version found, add transformations before the path
      // Handle case where afterUpload starts with /
      const cleanPath = afterUpload.startsWith('/') ? afterUpload.substring(1) : afterUpload;
      return `${beforeUpload}${transformations}/${cleanPath}`;
    }
  } else if (originalCloudinaryUrl.includes('/upload/')) {
    const uploadIndex = originalCloudinaryUrl.indexOf('/upload/');
    const beforeUpload = originalCloudinaryUrl.substring(0, uploadIndex + '/upload/'.length);
    const afterUpload = originalCloudinaryUrl.substring(uploadIndex + '/upload/'.length);
    
    // Look for version pattern (/v followed by digits)
    const versionMatch = afterUpload.match(/^(.*?)(\/?v\d+\/.*)/);
    if (versionMatch) {
      // URL has version: /upload/[existing_transforms]/v123456/path
      // Replace existing transforms with our new ones
      return `${beforeUpload}${transformations}/${versionMatch[2].replace(/^\//, '')}`;
    } else {
      // No version found, add transformations before the path
      // Handle case where afterUpload starts with /
      const cleanPath = afterUpload.startsWith('/') ? afterUpload.substring(1) : afterUpload;
      return `${beforeUpload}${transformations}/${cleanPath}`;
    }
  }
  
  console.warn('Could not parse Cloudinary URL format:', originalCloudinaryUrl);
  return originalCloudinaryUrl;
}

// Test function to verify coordinate transformation
export function testCoordinateTransform(cropCoords: CropCoordinates) {
  console.log('🧪 [Test] Testing coordinate transformation...');
  
  // Forward transform: image point -> screen point
  const testImagePoint = { x: 100, y: 200 };
  const screenPoint = {
    x: testImagePoint.x * cropCoords.scale + cropCoords.x,
    y: testImagePoint.y * cropCoords.scale + cropCoords.y
  };
  
  // Reverse transform: screen point -> image point  
  const backToImagePoint = {
    x: (screenPoint.x - cropCoords.x) / cropCoords.scale,
    y: (screenPoint.y - cropCoords.y) / cropCoords.scale
  };
  
  console.log('Test results:', {
    original: testImagePoint,
    toScreen: screenPoint,
    backToImage: backToImagePoint,
    isCorrect: testImagePoint.x === backToImagePoint.x && testImagePoint.y === backToImagePoint.y
  });
}

export async function cropImageToViewport(
  imageUrl: string, 
  cropCoords: CropCoordinates,
  debug = false
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Enable CORS for Cloudinary images
    
    img.onload = () => {
      try {
        // Create canvas with viewport dimensions
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          throw new Error('Could not get canvas context');
        }
        
        // Set canvas size to final crop size (viewport or inner crop if specified)
        const finalCropWidth = cropCoords.innerCrop 
          ? cropCoords.innerCrop.width * cropCoords.viewportWidth 
          : cropCoords.viewportWidth;
        const finalCropHeight = cropCoords.innerCrop 
          ? cropCoords.innerCrop.height * cropCoords.viewportHeight 
          : cropCoords.viewportHeight;
          
        canvas.width = finalCropWidth;
        canvas.height = finalCropHeight;
        
        // Match the exact viewport display logic
        const sourceWidth = img.naturalWidth;
        const sourceHeight = img.naturalHeight;
        
        // CSS Transform Analysis:
        // The image element has: transform: translate(x, y) scale(s) with transform-origin: 0 0
        // This means: first translate by (x, y), THEN scale by s from the origin
        // 
        // Example with image 1000x800, translate=(100, 50), scale=1.5:
        // 1. Image is moved to position (100, 50) 
        // 2. Then scaled by 1.5 from top-left corner of translated position
        // 3. Final image spans from (100, 50) to (100 + 1000*1.5, 50 + 800*1.5) = (100, 50) to (1600, 1250)
        //
        // To find what part of ORIGINAL image is visible in viewport (0,0) to (viewportWidth, viewportHeight):
        // 1. Viewport shows screen coordinates from (0, 0) to (viewportWidth, viewportHeight)
        // 2. Image spans from (x, y) to (x + originalWidth*scale, y + originalHeight*scale)
        // 3. Visible area in transformed space: intersection of viewport and image bounds
        // 4. Convert back to original image coordinates
        
        // COORDINATE TRANSFORMATION (CSS transforms apply right-to-left)
        // 
        // CSS Transform: transform: translate(x, y) scale(s) with transform-origin: 0 0
        // Order of operations: scale FIRST, then translate
        //
        // Forward transform: point (px, py) in image → screen point (sx, sy)
        // 1. Scale: (px, py) → (px * s, py * s)
        // 2. Translate: (px * s, py * s) → (px * s + x, py * s + y)  
        // Result: screen point = (px * s + x, py * s + y)
        //
        // Reverse transform: screen point (sx, sy) → image point (px, py) 
        // 1. Reverse translate: (sx, sy) → (sx - x, sy - y)
        // 2. Reverse scale: (sx - x, sy - y) → ((sx - x)/s, (sy - y)/s)
        // Result: image point = ((sx - x)/s, (sy - y)/s)
        
        if (debug) {
            console.log('🔍 [Debug] Starting coordinate calculation...');
            console.log('Transform params:', { x: cropCoords.x, y: cropCoords.y, scale: cropCoords.scale });
            console.log('Viewport size:', { width: cropCoords.viewportWidth, height: cropCoords.viewportHeight });
            console.log('Image natural size:', { width: sourceWidth, height: sourceHeight });
            
            // Test our coordinate transformation
            testCoordinateTransform(cropCoords);
            
            // Let's also check what the image looks like at current transform
            const imageScreenBounds = {
                left: cropCoords.x,
                top: cropCoords.y,
                right: cropCoords.x + (sourceWidth * cropCoords.scale),
                bottom: cropCoords.y + (sourceHeight * cropCoords.scale),
                width: sourceWidth * cropCoords.scale,
                height: sourceHeight * cropCoords.scale
            };
            console.log('Image bounds in screen space:', imageScreenBounds);
        }
        
        // SIMPLIFIED APPROACH: Use exact same calculation as debug overlay
        // This mirrors the calculation from the debug overlay which should match the viewport exactly
        const topLeftImageX = (0 - cropCoords.x) / cropCoords.scale;
        const topLeftImageY = (0 - cropCoords.y) / cropCoords.scale;
        const bottomRightImageX = (cropCoords.viewportWidth - cropCoords.x) / cropCoords.scale;
        const bottomRightImageY = (cropCoords.viewportHeight - cropCoords.y) / cropCoords.scale;
        
        // Simple crop coordinates - exactly what we calculated
        let sourceCropX = topLeftImageX;
        let sourceCropY = topLeftImageY;
        let sourceCropWidth = bottomRightImageX - topLeftImageX;
        let sourceCropHeight = bottomRightImageY - topLeftImageY;
        
        if (debug) {
            console.log('🔍 Viewport corners in image space:');
            console.log('Top-left image coords:', { x: topLeftImageX, y: topLeftImageY });
            console.log('Bottom-right image coords:', { x: bottomRightImageX, y: bottomRightImageY });
            console.log('Calculated crop area:', { x: sourceCropX, y: sourceCropY, width: sourceCropWidth, height: sourceCropHeight });
            
            // Verification: convert back to screen space to check
            const verifyTopLeftScreen = { 
                x: topLeftImageX * cropCoords.scale + cropCoords.x, 
                y: topLeftImageY * cropCoords.scale + cropCoords.y 
            };
            const verifyBottomRightScreen = { 
                x: bottomRightImageX * cropCoords.scale + cropCoords.x, 
                y: bottomRightImageY * cropCoords.scale + cropCoords.y 
            };
            console.log('🧪 Verification - should be (0,0) and (viewport):', {
                topLeft: verifyTopLeftScreen,
                bottomRight: verifyBottomRightScreen,
                expected: { topLeft: { x: 0, y: 0 }, bottomRight: { x: cropCoords.viewportWidth, y: cropCoords.viewportHeight } }
            });
        }
        
        // Apply inner crop if specified
        if (cropCoords.innerCrop) {
          // Inner crop is defined as percentages of the viewport
          // We need to adjust our source crop area accordingly
          const innerOffsetX = cropCoords.innerCrop.left * sourceCropWidth;
          const innerOffsetY = cropCoords.innerCrop.top * sourceCropHeight;
          const innerWidth = cropCoords.innerCrop.width * sourceCropWidth;
          const innerHeight = cropCoords.innerCrop.height * sourceCropHeight;
          
          sourceCropX = sourceCropX + innerOffsetX;
          sourceCropY = sourceCropY + innerOffsetY;
          sourceCropWidth = innerWidth;
          sourceCropHeight = innerHeight;
          
          console.log('📐 [Canvas Crop] Inner crop applied:', {
            originalCrop: { x: topLeftImageX, y: topLeftImageY, width: bottomRightImageX - topLeftImageX, height: bottomRightImageY - topLeftImageY },
            innerCropPercent: cropCoords.innerCrop,
            finalCrop: { x: sourceCropX, y: sourceCropY, width: sourceCropWidth, height: sourceCropHeight }
          });
        }
        
        if (debug) {
            console.log('🔍 Before bounds clamping:', { x: sourceCropX, y: sourceCropY, width: sourceCropWidth, height: sourceCropHeight });
            console.log('Image bounds:', { width: sourceWidth, height: sourceHeight });
        }
        
        // Handle coordinates outside image bounds by calculating proper offsets
        // Instead of clamping (which shifts the result), we calculate what part of the viewport
        // actually shows image data and position it correctly on the canvas
        
        const actualSourceX = Math.max(0, sourceCropX);
        const actualSourceY = Math.max(0, sourceCropY);
        const actualSourceRight = Math.min(sourceCropX + sourceCropWidth, sourceWidth);
        const actualSourceBottom = Math.min(sourceCropY + sourceCropHeight, sourceHeight);
        const actualSourceWidth = actualSourceRight - actualSourceX;
        const actualSourceHeight = actualSourceBottom - actualSourceY;
        
        // Calculate where this image portion appears in the viewport
        const canvasDestX = (actualSourceX - sourceCropX) * (finalCropWidth / sourceCropWidth);
        const canvasDestY = (actualSourceY - sourceCropY) * (finalCropHeight / sourceCropHeight);
        const canvasDestWidth = actualSourceWidth * (finalCropWidth / sourceCropWidth);
        const canvasDestHeight = actualSourceHeight * (finalCropHeight / sourceCropHeight);
        
        if (debug) {
            console.log('🔍 Proper coordinate handling:');
            console.log('Requested crop:', { x: sourceCropX, y: sourceCropY, width: sourceCropWidth, height: sourceCropHeight });
            console.log('Actual source (within image):', { x: actualSourceX, y: actualSourceY, width: actualSourceWidth, height: actualSourceHeight });
            console.log('Canvas destination:', { x: canvasDestX, y: canvasDestY, width: canvasDestWidth, height: canvasDestHeight });
            
            const hasOffset = sourceCropX < 0 || sourceCropY < 0;
            if (hasOffset) {
                console.log('✅ Handling viewport offset properly - no clamping shift!');
            }
        }
        
        console.log('🎯 [Canvas Crop] Final coordinate transformation:', {
          originalSize: { width: sourceWidth, height: sourceHeight },
          viewportSize: { width: cropCoords.viewportWidth, height: cropCoords.viewportHeight },
          transform: { x: cropCoords.x, y: cropCoords.y, scale: cropCoords.scale },
          cssTransform: `translate(${cropCoords.x}px, ${cropCoords.y}px) scale(${cropCoords.scale})`,
          coordinateMapping: {
            viewportCorners: { topLeft: [0, 0], bottomRight: [cropCoords.viewportWidth, cropCoords.viewportHeight] },
            originalImageCoords: { topLeft: [topLeftImageX, topLeftImageY], bottomRight: [bottomRightImageX, bottomRightImageY] }
          },
          actualSource: { x: actualSourceX, y: actualSourceY, width: actualSourceWidth, height: actualSourceHeight },
          canvasDest: { x: canvasDestX, y: canvasDestY, width: canvasDestWidth, height: canvasDestHeight },
          canvasOutput: { width: finalCropWidth, height: finalCropHeight }
        });
        
        // Clear canvas with white background first
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, finalCropWidth, finalCropHeight);
        
        // Draw the image portion at the correct position to maintain viewport alignment
        if (actualSourceWidth > 0 && actualSourceHeight > 0) {
            ctx.drawImage(
                img,
                actualSourceX, actualSourceY, actualSourceWidth, actualSourceHeight, // Source: actual image portion
                canvasDestX, canvasDestY, canvasDestWidth, canvasDestHeight           // Destination: positioned to match viewport
            );
        }
        
        if (debug) {
            console.log('🎨 DrawImage call:', {
                source: { x: actualSourceX, y: actualSourceY, width: actualSourceWidth, height: actualSourceHeight },
                destination: { x: canvasDestX, y: canvasDestY, width: canvasDestWidth, height: canvasDestHeight }
            });
        }
        
        // Convert canvas to blob
        canvas.toBlob((blob) => {
          if (blob) {
            console.log('✅ [Canvas Crop] Successfully created cropped image blob');
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        }, 'image/jpeg', 0.9);
        
      } catch (error) {
        console.error('❌ [Canvas Crop] Error cropping image:', error);
        reject(error);
      }
    };
    
    img.onerror = (error) => {
      console.error('❌ [Canvas Crop] Error loading image:', error);
      reject(new Error('Failed to load image for cropping'));
    };
    
    img.src = imageUrl;
  });
}

/**
 * Gets the display image URL - prioritizes annotated over cropped over original
 * @param asset - The asset object
 * @returns The best image URL to display
 */
export function getDisplayImageUrl(asset: { 
  // New organized fields (priority)
  primary_markup_url?: string;
  primary_cropped_url?: string;
  primary_original_url?: string;
  // Legacy fields (fallback)
  primary_image?: string; 
}): string {
  // Priority: markup -> cropped -> original
  return asset.primary_markup_url ||
         asset.primary_cropped_url ||
         asset.primary_original_url ||
         asset.primary_image || '';
}

/**
 * Gets the secondary display image URL - prioritizes annotated over cropped over original
 * @param asset - The asset object
 * @returns The best secondary image URL to display
 */
export function getSecondaryDisplayImageUrl(asset: {
  // New organized fields (priority) 
  secondary_markup_url?: string;
  secondary_cropped_url?: string;
  secondary_original_url?: string;
  // Legacy field (fallback)
  secondary_image?: string;
}): string {
  // Priority: markup -> cropped -> original
  return asset.secondary_markup_url ||
         asset.secondary_cropped_url ||
         asset.secondary_original_url ||
         // Fallback for backward compatibility
         asset.secondary_image || '';
}

/**
 * Gets the primary original uncropped image URL for editing
 * @param asset - The asset object  
 * @returns The primary original image URL
 */
export function getPrimaryOriginalUrl(asset: {
  primary_original_url?: string;
  primary_image?: string;
}): string {
  // CRITICAL: Always prioritize original URLs for editing - never use cropped versions
  if (asset.primary_original_url) {
    return asset.primary_original_url;
  }
  
  
  // Emergency fallback
  if (asset.primary_image) {
    return asset.primary_image;
  }
  
  console.error('❌ [getPrimaryOriginalUrl] No image URL found at all');
  return '';
}

/**
 * Gets the secondary original uncropped image URL for editing
 * @param asset - The asset object
 * @returns The secondary original image URL
 */
export function getSecondaryOriginalUrl(asset: {
  secondary_original_url?: string;
  secondary_image?: string;
}): string {
  if (asset.secondary_original_url) {
    console.log('✅ [getSecondaryOriginalUrl] Using secondary_original_url (unmodified source):', asset.secondary_original_url);
    return asset.secondary_original_url;
  }
  
  // Emergency fallback - secondary_image might be original or cropped
  if (asset.secondary_image) {
    console.warn('⚠️ [getSecondaryOriginalUrl] FALLBACK: Using secondary_image, but this may be a cropped version!');
    return asset.secondary_image;
  }
  
  console.log('ℹ️ [getSecondaryOriginalUrl] No secondary original URL found (this is normal for single-view assets)');
  return '';
}

/**
 * Convert S3 URLs to Cloudinary URLs to avoid CORS issues
 * This handles the conversion from S3 URLs to Cloudinary proxy URLs
 */
export function convertToCloudinaryUrl(url: string): string {
  if (!url) return '';
  
  // If already a Cloudinary URL, return as-is
  if (url.includes('cloudinary.com') || url.includes('res.cloudinary.com')) {
    console.log('✅ [convertToCloudinaryUrl] Already a Cloudinary URL:', url);
    return url;
  }
  
  // If it's an S3 URL, convert it to Cloudinary proxy
  if (url.includes('s3.us-east-1.amazonaws.com') || url.includes('amazonaws.com')) {
    // Convert to Cloudinary proxy URL
    const cloudinaryUrl = `https://res.cloudinary.com/pricing-explorer/image/fetch/${encodeURIComponent(url)}`;
    console.log('🔄 [convertToCloudinaryUrl] Converting S3 URL to Cloudinary proxy:', {
      original: url,
      converted: cloudinaryUrl
    });
    return cloudinaryUrl;
  }
  
  // If it's any other URL, try Cloudinary proxy
  if (url.startsWith('http://') || url.startsWith('https://')) {
    const cloudinaryUrl = `https://res.cloudinary.com/pricing-explorer/image/fetch/${encodeURIComponent(url)}`;
    console.log('🔄 [convertToCloudinaryUrl] Converting external URL to Cloudinary proxy:', {
      original: url,
      converted: cloudinaryUrl
    });
    return cloudinaryUrl;
  }
  
  console.warn('⚠️ [convertToCloudinaryUrl] Could not convert URL:', url);
  return url;
}

/**
 * Gets the primary original URL with Cloudinary conversion for CORS compatibility
 */
export function getPrimaryOriginalUrlCloudinary(asset: {
  primary_original_url?: string;
  primary_image?: string;
}): string {
  const originalUrl = getPrimaryOriginalUrl(asset);
  return convertToCloudinaryUrl(originalUrl);
}

/**
 * Gets the secondary original URL with Cloudinary conversion for CORS compatibility
 */
export function getSecondaryOriginalUrlCloudinary(asset: {
  secondary_original_url?: string;
  secondary_image?: string;
}): string {
  const originalUrl = getSecondaryOriginalUrl(asset);
  return convertToCloudinaryUrl(originalUrl);
}

/**
 * Legacy function - kept for backward compatibility
 * @deprecated Use getPrimaryOriginalUrl instead
 */
export function getOriginalImageUrl(asset: {
  primary_original_url?: string;
  primary_image?: string;
}): string {
  console.warn('⚠️ [getOriginalImageUrl] This function is deprecated. Use getPrimaryOriginalUrl instead.');
  return getPrimaryOriginalUrl(asset);
}